import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import EquityLogo from "@/components/EquityLogo";
import { useBranding } from "@/hooks/useBranding";

export default function Login() {
  const { login, user } = useAuth();
  const { companyName } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const quick = (em, pw) => { setEmail(em); setPassword(pw); };
  // Demo-account shortcuts must never appear on a real deployment — gate strictly on
  // localhost so this can't accidentally ship visible on the production domain.
  const isLocalDev = ["localhost", "127.0.0.1"].includes(window.location.hostname);

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 relative overflow-hidden bg-[#09090B]">
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-blue-700/20 blur-[120px]" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-blue-900/30 blur-[120px]" />
      <div className="w-full max-w-md relative fade-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#020617] overflow-hidden flex items-center justify-center shadow-[0_0_24px_rgba(37,99,235,0.4)] shrink-0">
            <EquityLogo size={56} />
          </div>
          <div>
            <h1 className="font-heading text-lg font-medium tracking-[0.18em] uppercase leading-none text-zinc-200">
              Equity<span className="text-blue-400">.CRM</span>
            </h1>
            <p className="text-xs text-zinc-500 mt-1.5">{companyName}</p>
          </div>
        </div>

        <div className="card-surface rounded-3xl p-7">
          <h2 className="text-2xl font-bold tracking-tight mb-1">Selamat datang 👋</h2>
          <p className="text-sm text-zinc-500 mb-6">Masuk untuk kelola leads & pipeline kamu.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wide">Email</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@perusahaan.com" className="mt-1.5 bg-[#18181B] border-white/10 h-11" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wide">Password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="login-password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="bg-[#18181B] border-white/10 h-11 pr-11" />
                <button type="button" data-testid="login-password-toggle" onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-0 top-0 h-11 w-11 flex items-center justify-center text-zinc-500 hover:text-zinc-300"
                  tabIndex={-1} aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}>
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-11 bg-gradient-to-r from-blue-700 to-blue-900 hover:opacity-90 font-semibold">
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Masuk"}
            </Button>
          </form>

          {isLocalDev && (
            <div className="mt-6 rounded-xl bg-[#18181B] border border-white/5 p-3">
              <p className="text-xs text-zinc-500 mb-2">Akun demo (klik untuk isi) — hanya tampil di localhost:</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => quick("sales@example.com", "changeme123")} className="text-xs px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-300 border border-blue-500/20">Sales</button>
                <button type="button" onClick={() => quick("admin@example.com", "changeme123")} className="text-xs px-2.5 py-1 rounded-lg bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">Admin</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
